/**
 * types.ts
 * フロー全体で使用する型定義の中心ファイル
 * 他ファイルはここから型をインポートすることで一貫性を保つ
 */
import type { Node, Edge, NodeProps } from '@xyflow/react';
import type { ComponentType } from 'react';
import type { BatchCommandSummary, FlowCommandOptions } from '@/connection/BatchRobotService';
import type { SpeechRequest } from '@/api/types';
import type { FlowMotionInfo } from './atoms/flowMotionAtom';
import type { FlowSayTextInfo } from './atoms/flowSayTextAtom';
import type { SoundEffect } from './config/soundEffects';
import type { CustomMotionConfig } from './config/customMotions';
import type { WalkingDisplacement } from './config/walkingMotions';
import type { LightColor, LightPayload } from './config/lightPresets';
import type { SpeechPrefetch, SpeechSynthesizer } from '@/speech/SpeechPrefetch';

// ============ データ型 ============

/** 全ノード共通のデータ構造 */
type BaseNodeData = { label: string };

export type IfOperator = '<' | '<=' | '=' | '=>' | '>' | '≠';

/**
 * 各ノード種別のデータ構造を定義するマップ
 * 新しいノードを追加する場合はここにエントリを追加する
 * → NodeKind, AppNode などはこのマップから自動派生される
 */
export type NodeDataMap = {
  start: BaseNodeData;
  end: BaseNodeData;
  input: BaseNodeData;
  output: BaseNodeData;
  function: BaseNodeData & {
    functionName: string;
    internalNodes: AppNode[];
    internalEdges: AppEdge[];
  };
  textEdit: BaseNodeData & { text: string };   // テキスト入力用
  sayText: BaseNodeData & {
    speaker: number;
    speedScale: number;
    pitchScale: number;
  };
  gesture: BaseNodeData & { motionId: string };      // モーション選択用
  dance: BaseNodeData & { motionId: string };        // ダンス選択用
  qrScan: BaseNodeData;
  tof: BaseNodeData;
  listen: BaseNodeData;
  if: BaseNodeData & {
    operator: IfOperator;
    compareValue: string;
  };
  switch: BaseNodeData & {
    matchValue1: string;
    matchValue2: string;
  };
  counter: BaseNodeData & {
    initialValue: number;
    stepValue: number;
    finalValue: number;
  };   // 反復カウンタ用
  coinFlip: BaseNodeData;
  wait: BaseNodeData & {
    seconds: number;
  };
  randomChoice: BaseNodeData & {
    choice1: string;
    choice2: string;
    choice3: string;
  };
  keywordSwitch: BaseNodeData & {
    keyword1: string;
    keyword2: string;
  };
  soundEffect: BaseNodeData & {
    sound: SoundEffect;
  };
  light: BaseNodeData & {
    color: LightColor;
  };
  mazeGoal: BaseNodeData;
  customGesture: BaseNodeData & { motionId: string };  // 自作ジェスチャー選択用
  walking: BaseNodeData & { motionId: string; stepCount?: number };
};

/** ノード種別の文字列リテラル型（NodeDataMapのキーから自動派生） */
export type NodeKind = keyof NodeDataMap;

// ============ ノード型 ============

/** 特定種別のノード型を生成するジェネリック型（Kがユニオンなら対応するノード型のユニオンになる） */
export type AppNodeOf<K extends NodeKind> =
  { [T in NodeKind]: Node<NodeDataMap[T], T> }[K];

/** 全ノード種別のユニオン型（Discriminated Union パターン） */
export type AppNode = { [K in NodeKind]: AppNodeOf<K> }[NodeKind];

/** エッジ（ノード間の接続線）の型 */
export type AppEdge = Edge;

// ============ 実行コンテキスト ============

export type QrScanResult =
  | { status: 'success'; value: string }
  | { status: 'cancelled' }
  | { status: 'timeout' }
  | { status: 'error'; message: string };

export type QrScanner = {
  scan: () => Promise<QrScanResult>;
};

export type ListenResult =
  | { status: 'success'; value: string }
  | { status: 'cancelled' }
  | { status: 'timeout' }
  | { status: 'error'; message: string };

export type SpeechListener = {
  listen: () => Promise<ListenResult>;
};

export type FlowExecutionEvent =
  | { type: 'node-started'; nodeId: string; nodeKind: NodeKind }
  | { type: 'node-finished'; nodeId: string; nodeKind: NodeKind; value?: unknown }
  | { type: 'node-error'; nodeId: string; nodeKind: NodeKind; message: string }
  | { type: 'speech'; nodeId: string; text: string }
  | { type: 'motion'; nodeId: string; nodeKind: 'gesture' | 'customGesture' | 'dance' | 'walking'; motionId: string }
  | { type: 'light'; nodeId: string; color: string }
  | { type: 'tof'; nodeId: string; distanceMm: number }
  | { type: 'collision'; nodeId: string }
  | { type: 'goal-reached'; nodeId: string }
  | { type: 'terminal-reached'; nodeId: string }
  | { type: 'run-error'; message: string };

/**
 * フロー実行時にノード間でデータを受け渡すためのコンテキスト
 * 例: TextEditで設定した値をSayTextで読み取る
 */
export type ExecContext = {
  lastValue?: unknown;  // 直前のノードから渡された値
  onExecutionEvent?: (event: FlowExecutionEvent) => void;
  robotService?: {
    synthesizeSpeech?: SpeechSynthesizer;
    speak(
      data: { text: string; speaker?: number; speedScale?: number; pitchScale?: number },
      options: FlowCommandOptions,
    ): Promise<BatchCommandSummary>;
    startMotion(motionNo: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkForwardSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkBackwardSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkLeftSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkRightSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkCurveRightSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    walkCurveLeftSteps?(steps: number, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    playCustomGesture(motion: CustomMotionConfig, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    getCurrentAngles(vids: number[], options: FlowCommandOptions): Promise<BatchCommandSummary<Record<string, number | null>>>;
    measureTOF?(data: { mode: 0 | 1 | 2; area: number }, options: FlowCommandOptions): Promise<BatchCommandSummary<number>>;
    playSound(data: { sound: SoundEffect }, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    setLed(data: LightPayload, options: FlowCommandOptions): Promise<BatchCommandSummary>;
    cleanupLed?(options: Pick<FlowCommandOptions, 'nodeId' | 'timeoutMs'>): Promise<BatchCommandSummary>;
    hasActiveTargets(): boolean;
  };
  random?: () => number;
  signal?: AbortSignal;
  getRunStopReason?: () => string | null;
  qrScanner?: QrScanner;
  listener?: SpeechListener;
  // Counterノードの実行時状態（run.ts 内部制御用）
  counterStates?: Record<string, { current: number; iteration: number }>;
  // VirtualRobot でモーション再生するための関数（Gesture カードから呼び出される）
  // onComplete コールバックはモーション完了時に呼ばれる
  setFlowMotion?: (motion: FlowMotionInfo | null) => void;
  // VirtualRobot でふきだし表示するための関数（SayText ノードから呼び出される）
  // onComplete コールバックはふきだし表示完了時に呼ばれる
  setSayText?: (sayText: FlowSayTextInfo | null) => void;
  playSimulationSound?: (sound: SoundEffect, signal?: AbortSignal) => Promise<void>;
  playSimulationSpeech?: (data: SpeechRequest, signal?: AbortSignal) => Promise<void>;
  synthesizeSpeech?: SpeechSynthesizer;
  speechPrefetch?: SpeechPrefetch;
  setSimulationLightColor?: (displayColor: string) => void;
  captureFunctionOutput?: (value: unknown) => void;
  setTofScan?: (tof: { mode: 1; area: 0x16; requestId: number; value: number; sampleIndex: number } | null) => void;
  setNodeRuntimeState?: (nodeId: string, state: 'blocked' | null) => void;
  measureSimulationTof?: () => number;
  // 迷路アプリでのみ設定される。歩行モーション後の位置・向きを迷路へ反映する
  mazeControl?: {
    canApplyWalkingMotion(motionNo: number, displacement?: WalkingDisplacement): boolean;
    applyWalkingMotion(motionNo: number, displacement?: WalkingDisplacement): Promise<{ blocked: boolean; goalReached: boolean }>;
    isAtGoal?(): boolean;
    measureDistanceMm?(maxDistanceMm: number): number;
    continueFlowAfterGoal?: boolean;
  };
};

/** ログ出力用のコールバック関数型 */
export type Logger = (line: string) => void;

/** run 関数に渡される付帯情報 */
export type NodeRunMeta = {
  /** このノードに入ってきたエッジの targetHandle（無ければ null） */
  incomingHandle?: string | null;
};

/**
 * run 関数の戻り値。
 * void は全出力へ進み、nextHandle は特定出力への分岐、null は枝の終了を表す。
 */
export type NodeRunResult =
  | void
  | {
      nextHandle: string | null;
      /** 「つながる線がありません」警告に使う表示名 */
      branchLabel?: string;
    };

// ============ ルビ型 ============

/**
 * ルビ（ふりがな）表示用のパーツ型
 * 通常テキストとルビ付きテキストを区別する
 */
export type RubyPart =
  | { kind: 'text'; text: string }                      // 通常テキスト
  | { kind: 'ruby'; base: string; ruby: string };       // ルビ付き（例: 漢字 + ふりがな）

// ============ ノード定義型 ============

/** 設定ダイアログの中身に渡されるプロパティ */
export type NodeSettingsProps<K extends NodeKind> = {
  node: AppNodeOf<K>;
};

/**
 * ノード種別ごとの定義構造
 * 表示、実行、ヘルプなどノードに関する全設定を含む
 */
export type NodeDefinition<K extends NodeKind> = {
  label: string;              // パレットや表示用のラベル
  showInPalette: boolean;     // パレットに表示するか（Start/Endはfalse）
  mazeOnly?: boolean;         // 迷路アプリのパレットだけに表示するか
  paletteImageKind?: NodeKind;
  paletteColor: string;       // パレット表示時のTailwindクラス
  miniMapColor: string;       // ミニマップ上の色
  defaultData: NodeDataMap[K]; // 新規作成時のデフォルトデータ
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  view: ComponentType<NodeProps<any>>;  // 表示用Reactコンポーネント
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings?: ComponentType<{ node: any }>;  // 設定ダイアログの中身（設定項目が無いカードは省略）
  run: (node: AppNodeOf<K>, ctx: ExecContext, log: Logger, meta?: NodeRunMeta) => Promise<NodeRunResult> | NodeRunResult;  // 実行関数
  loopEntryHandle?: string;  // ループとして許可する入力ハンドル
  isTerminal?: boolean;      // trueならこのノードで枝を終える
  help: {
    title: RubyPart[];        // ヘルプパネルのタイトル
    description: RubyPart[];  // ヘルプパネルの説明文
    example?: RubyPart[];     // 使用例（オプション）
  };
};

// ============ 後方互換性のための型エイリアス ============
// 個別ファイルで直接使えるように各ノードのデータ型/ノード型をエクスポート

export type StartData = NodeDataMap['start'];
export type EndData = NodeDataMap['end'];
export type InputData = NodeDataMap['input'];
export type OutputData = NodeDataMap['output'];
export type FunctionData = NodeDataMap['function'];
export type TextEditData = NodeDataMap['textEdit'];
export type SayTextData = NodeDataMap['sayText'];
export type GestureData = NodeDataMap['gesture'];
export type DanceData = NodeDataMap['dance'];
export type QrScanData = NodeDataMap['qrScan'];
export type TofData = NodeDataMap['tof'];
export type ListenData = NodeDataMap['listen'];
export type IfData = NodeDataMap['if'];
export type SwitchData = NodeDataMap['switch'];
export type CounterData = NodeDataMap['counter'];
export type CoinFlipData = NodeDataMap['coinFlip'];
export type WaitData = NodeDataMap['wait'];
export type RandomChoiceData = NodeDataMap['randomChoice'];
export type KeywordSwitchData = NodeDataMap['keywordSwitch'];
export type SoundEffectData = NodeDataMap['soundEffect'];
export type LightData = NodeDataMap['light'];
export type CustomGestureData = NodeDataMap['customGesture'];
export type WalkingData = NodeDataMap['walking'];

export type StartNode = AppNodeOf<'start'>;
export type EndNode = AppNodeOf<'end'>;
export type InputNode = AppNodeOf<'input'>;
export type OutputNode = AppNodeOf<'output'>;
export type FunctionNode = AppNodeOf<'function'>;
export type TextEditNode = AppNodeOf<'textEdit'>;
export type SayTextNode = AppNodeOf<'sayText'>;
export type GestureNode = AppNodeOf<'gesture'>;
export type DanceNode = AppNodeOf<'dance'>;
export type QrScanNode = AppNodeOf<'qrScan'>;
export type TofNode = AppNodeOf<'tof'>;
export type ListenNode = AppNodeOf<'listen'>;
export type IfNode = AppNodeOf<'if'>;
export type SwitchNode = AppNodeOf<'switch'>;
export type CounterNode = AppNodeOf<'counter'>;
export type CoinFlipNode = AppNodeOf<'coinFlip'>;
export type WaitNode = AppNodeOf<'wait'>;
export type RandomChoiceNode = AppNodeOf<'randomChoice'>;
export type KeywordSwitchNode = AppNodeOf<'keywordSwitch'>;
export type SoundEffectNode = AppNodeOf<'soundEffect'>;
export type LightNode = AppNodeOf<'light'>;
export type CustomGestureNode = AppNodeOf<'customGesture'>;
export type WalkingNode = AppNodeOf<'walking'>;

// ============ Type Guards ============

/** TextEditノードかどうかを判定する型ガード */
export const isTextEditNode = (node: AppNode): node is TextEditNode =>
  node.type === 'textEdit';

/** Gestureカードかどうかを判定する型ガード */
export const isGestureNode = (node: AppNode): node is GestureNode =>
  node.type === 'gesture';

/** Danceカードかどうかを判定する型ガード */
export const isDanceNode = (node: AppNode): node is DanceNode =>
  node.type === 'dance';

/** Counterノードかどうかを判定する型ガード */
export const isCounterNode = (node: AppNode): node is CounterNode =>
  node.type === 'counter';

export const isIfNode = (node: AppNode): node is IfNode =>
  node.type === 'if';

/** Switchカードかどうかを判定する型ガード */
export const isSwitchNode = (node: AppNode): node is SwitchNode =>
  node.type === 'switch';

/** Waitカードかどうかを判定する型ガード */
export const isWaitNode = (node: AppNode): node is WaitNode =>
  node.type === 'wait';

/** Random Choiceカードかどうかを判定する型ガード */
export const isRandomChoiceNode = (node: AppNode): node is RandomChoiceNode =>
  node.type === 'randomChoice';

/** Keyword Switchカードかどうかを判定する型ガード */
export const isKeywordSwitchNode = (node: AppNode): node is KeywordSwitchNode =>
  node.type === 'keywordSwitch';

/** Sound Effectカードかどうかを判定する型ガード */
export const isSoundEffectNode = (node: AppNode): node is SoundEffectNode =>
  node.type === 'soundEffect';

/** Lightカードかどうかを判定する型ガード */
export const isLightNode = (node: AppNode): node is LightNode =>
  node.type === 'light';
